import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, Text } from 'rebass/styled-components';
import { Button, FormInput, Message, Modal } from '@getro/rombo';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { emailRegEx } from '../../../helpers/regExs';

const validationSchema = Yup.object().shape({
  firstName: Yup.string().label('First name').required('Required'),
  lastName: Yup.string().label('Last name'),
  email: Yup.string().trim().matches(emailRegEx, 'Not a valid email').required('Required'),
});

const initialValues = {
  firstName: '',
  lastName: '',
  email: '',
};

const ROLE_CONFIG = {
  admin: {
    title: 'Invite an admin',
    description: 'Admins have full access to your network, including managing data, settings, and team members.',
    submitLabel: 'Invite admin',
    testId: 'invite-admin',
  },
  ally: {
    title: 'Invite an ally',
    description: 'Allies can share their contacts with your network, and have no access to other features.',
    submitLabel: 'Invite ally',
    testId: 'invite-ally',
  },
};

export const InviteMemberModal = ({ memberRole, isSubmitting, onInvite, setModalOpen, adminsEmails, error }) => {
  const formRef = useRef(null);
  const config = ROLE_CONFIG[memberRole];

  const isEmailAlreadyInvited = (email) => adminsEmails.includes(email);

  return (
    <Modal pauseFocusTrap title={config.title} isOpen onCancel={() => setModalOpen(false)} data-testid={config.testId}>
      <Flex flexDirection="column" sx={{ gap: '24px' }}>
        <Text fontSize="14px" color="text.main" lineHeight="1.4">
          {config.description}
        </Text>
        <Formik
          innerRef={formRef}
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            if (isEmailAlreadyInvited(values.email)) return;

            onInvite({
              firstName: values.firstName,
              lastName: values.lastName,
              email: values.email,
            });
          }}
        >
          {({ values, errors }) => (
            <Form>
              <Flex sx={{ flexDirection: 'column', gap: '24px' }}>
                <Box>
                  <Field
                    required
                    name="firstName"
                    error={values.firstName && errors.firstName}
                    value={values.firstName}
                    type="text"
                    label="Name"
                    component={FormInput}
                    labelExtra="Required"
                  />
                </Box>
                <Box>
                  <Field
                    name="lastName"
                    error={errors.lastName}
                    value={values.lastName}
                    type="text"
                    label="Last name"
                    component={FormInput}
                  />
                </Box>
                <Box width={1}>
                  <Field
                    required
                    name="email"
                    error={values.email && errors.email}
                    value={values.email}
                    type="text"
                    label="Email"
                    labelExtra="Required"
                    component={FormInput}
                  />
                </Box>
                <Flex
                  sx={{
                    flexDirection: 'column',
                    gap: '16px',
                    borderTop: '1px solid',
                    pt: '16px',
                    borderColor: 'neutral.100',
                  }}
                >
                  {isEmailAlreadyInvited(values.email) && (
                    <Message content={`${values.email} is already a team member.`} />
                  )}
                  {error && (
                    <Message type="error" content="There was an unexpected error, please try again or contact us." />
                  )}
                  <Flex justifyContent={['flex-end']}>
                    <Button mr={[2]} onClick={() => setModalOpen(false)} variant="tertiary">
                      Cancel
                    </Button>
                    <Button ml={[2]} loading={isSubmitting} type="submit">
                      {config.submitLabel}
                    </Button>
                  </Flex>
                </Flex>
              </Flex>
            </Form>
          )}
        </Formik>
      </Flex>
    </Modal>
  );
};

InviteMemberModal.propTypes = {
  memberRole: PropTypes.oneOf(['admin', 'ally']).isRequired,
  onInvite: PropTypes.func.isRequired,
  setModalOpen: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  adminsEmails: PropTypes.arrayOf(PropTypes.string),
  error: PropTypes.bool,
};

InviteMemberModal.defaultProps = {
  isSubmitting: false,
  adminsEmails: [],
  error: false,
};
